import Handsontable from 'handsontable/base';
import {
    getEndCol,
    getEndRow,
    getFirstSelection,
    getStartCol,
    getStartRow,
} from '../../selectionUtils/tableSelectionAccessors';
import {
    getNColsInSelection,
    getNRowsInSelection,
    unselectedColCount,
    unselectedRowCount,
} from '../../selectionUtils/tableSelectionCountUtils';
import { moveCellsDown, moveCellsRight } from '../../selectionUtils/tableSelectionMovementUtils';
import {
    isSelectingMultipleSelections,
    isSelectingRefColHeader,
    isSelectingRefRowHeader,
} from '../../selectionUtils/tableSelectionPredicateUtils';
import { updateHotTableCellSelections } from '../selection/hotTableSelectionMutationUtils';
import { buildHotTableContentData } from '../hotTableDataUtils';
import { TABLE_MIN_COL_COUNT, TABLE_MIN_ROW_COUNT } from '../../../../../../common/table/tableConstants';
import { TableChangeSource } from '../../../../../../common/table/TableTypes';

/**
 * Adds columns to the right of the current cell selection in the Handsontable instance.
 */
export const addHotTableColsRight = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    const source = TableChangeSource.ADD_COLS_RIGHT;

    if (!cellSelection) {
        hotInstance.alter('insert_col_end', undefined, undefined, source);
        return;
    }

    const firstCellSelection = getFirstSelection(cellSelection);
    if (!firstCellSelection) return;

    const isRefRowHeaderSelected = isSelectingRefRowHeader(firstCellSelection);
    if (isRefRowHeaderSelected) {
        const index = getNColsInSelection(firstCellSelection) - 1;
        const amount = 1;

        hotInstance.alter('insert_col_start', index, amount, source);
    } else {
        const index = Math.max(getStartCol(firstCellSelection), getEndCol(firstCellSelection)) + 1;
        const amount = getNColsInSelection(firstCellSelection);

        hotInstance.alter('insert_col_start', index, amount, source);
    }

    updateHotTableCellSelections(hotInstance, [moveCellsRight(firstCellSelection)]);
};

/**
 * Adds columns to the left of the current cell selection in the Handsontable instance, then moves
 * the selection to remain on the same cells as before (as their index has now increased).
 */
export const addHotTableColsLeft = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    const isAddingColsLeftAllowed = !isSelectingMultipleSelections(cellSelection);
    if (!isAddingColsLeftAllowed) return;

    const source = TableChangeSource.ADD_COLS_LEFT;

    const firstCellSelection = getFirstSelection(cellSelection);

    if (!firstCellSelection) {
        // If no cell selection, add col to the start
        hotInstance.alter('insert_col_start', undefined, undefined, source);
        return;
    }

    const isRefRowHeaderSelected = isSelectingRefRowHeader(firstCellSelection);
    if (isRefRowHeaderSelected) {
        const index = 0;
        const amount = 1;

        hotInstance.alter('insert_col_start', index, amount, source);
        updateHotTableCellSelections(hotInstance, [
            [getStartRow(firstCellSelection), 0, getEndRow(firstCellSelection), 0],
        ]);
        return;
    }

    const index = Math.min(getStartCol(firstCellSelection), getEndCol(firstCellSelection));
    const amount = getNColsInSelection(firstCellSelection);

    hotInstance.alter('insert_col_start', index, amount, source);
    updateHotTableCellSelections(hotInstance, [firstCellSelection]);
};

/**
 * Determines if removing columns from the current cell selection in the Handsontable instance is disabled.
 */
export const isHotTableColRemovalDisabled = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
): boolean => {
    if (!hotInstance) return true;

    const hotTableData = buildHotTableContentData(hotInstance);

    const firstCellSelection = getFirstSelection(cellSelection);

    if (!hotTableData || !firstCellSelection) return true;

    return (
        isSelectingMultipleSelections(cellSelection) ||
        unselectedColCount(hotTableData, firstCellSelection) < TABLE_MIN_COL_COUNT
    );
};

/**
 * Removes columns from the current cell selection in the Handsontable instance.
 */
export const removeHotTableCols = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    if (isHotTableColRemovalDisabled(hotInstance, cellSelection)) return;

    const firstCellSelection = getFirstSelection(cellSelection);
    if (!firstCellSelection) return;

    const index = Math.min(getStartCol(firstCellSelection), getEndCol(firstCellSelection));
    const amount = getNColsInSelection(firstCellSelection);

    hotInstance.alter('remove_col', index, amount, TableChangeSource.REMOVE_COLS);
};

/**
 * Adds rows above the current cell selection in the Handsontable instance, then moves
 * the selection to remain on the same cells as before (as their index has now increased).
 */
export const addHotTableRowsAbove = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    const isAddingRowsAboveAllowed = !isSelectingMultipleSelections(cellSelection);
    if (!isAddingRowsAboveAllowed) return;

    const source = TableChangeSource.ADD_ROWS_ABOVE;

    const firstCellSelection = getFirstSelection(cellSelection);

    if (!firstCellSelection) {
        // If no cell selection, add row to the start
        hotInstance.alter('insert_row_above', undefined, undefined, source);
        return;
    }

    const isRefColHeaderSelected = isSelectingRefColHeader(firstCellSelection);
    if (isRefColHeaderSelected) {
        hotInstance.alter('insert_row_above', 0, 1, source);

        updateHotTableCellSelections(hotInstance, [
            [0, getStartCol(firstCellSelection), 0, getEndCol(firstCellSelection)],
        ]);
        return;
    }

    const index = Math.min(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
    const amount = getNRowsInSelection(firstCellSelection);

    hotInstance.alter('insert_row_above', index, amount, source);
    updateHotTableCellSelections(hotInstance, [firstCellSelection]);
};

/**
 * Adds rows below the current cell selection in the Handsontable instance.
 */
export const addHotTableRowsBelow = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    const source = TableChangeSource.ADD_ROWS_BELOW;

    if (isSelectingMultipleSelections(cellSelection)) return;

    if (!cellSelection) {
        hotInstance.alter('insert_row_below', undefined, undefined, source);
        return;
    }

    const firstCellSelection = getFirstSelection(cellSelection);
    if (!firstCellSelection) return;

    const isRefColHeaderSelected = isSelectingRefColHeader(firstCellSelection);
    if (isRefColHeaderSelected) {
        const index = getNRowsInSelection(firstCellSelection) - 1;
        const amount = 1;

        hotInstance.alter('insert_row_below', index, amount, source);
    } else {
        const index = Math.max(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
        const amount = getNRowsInSelection(firstCellSelection);

        hotInstance.alter('insert_row_below', index, amount, source);
    }

    updateHotTableCellSelections(hotInstance, [moveCellsDown(firstCellSelection)]);
};

/**
 * Determines if removing rows from the current cell selection in the Handsontable instance is disabled.
 */
export const isHotTableRowRemovalDisabled = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
): boolean => {
    if (!hotInstance) return true;

    const hotTableData = buildHotTableContentData(hotInstance);

    const firstCellSelection = getFirstSelection(cellSelection);

    if (!hotTableData || !firstCellSelection) return true;

    return unselectedRowCount(hotTableData, firstCellSelection) < TABLE_MIN_ROW_COUNT;
};

/**
 * Removes the currently selected row.
 */
export const removeHotTableRows = (
    hotInstance: Handsontable | null | undefined,
    cellSelection = hotInstance?.getSelected(),
) => {
    if (!hotInstance) return;

    if (isHotTableRowRemovalDisabled(hotInstance, cellSelection)) return;

    const firstCellSelection = getFirstSelection(cellSelection);
    if (!firstCellSelection) return;

    const index = Math.min(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
    const amount = getNRowsInSelection(firstCellSelection);

    hotInstance.alter('remove_row', index, amount, TableChangeSource.REMOVE_ROWS);
};
